<template>
    <v-form @submit.prevent="saveDocumentVersion" ref="form">
        <v-card class="elevation-1 rounded-4">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">{{ mode }} Version</span>
                <v-spacer></v-spacer>
                 <v-btn icon @click.native="closeDialog" :disabled="disabled">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-progress-linear v-if="loading_uploading" indeterminate height="2" class="mt-0"></v-progress-linear>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                Error encountered. Please contact support. {{errorMessage}}
            </v-alert>  

            <v-card-text>
                <!-- Version Label, Recipient-->
                <v-layout row wrap>
                    <v-flex xs12 sm8 class="form-content">
                        <v-text-field label="Version Label"
                            v-model="versionLabel"
                            :rules="[rules.required]"
                            maxlength="100"
                            hint="Max 100 alphanumeric characters"
                            :disabled="disabled"></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm4 class="form-content">
                        <v-select
                            label="Recipient"
                            :items="recipient_types"
                            :rules="[rules.required]" 
                            :disabled="disabled"
                            item-text="description"
                            item-value="recipientType"
                            v-model="recipientType"
                        ></v-select>
                    </v-flex>
                </v-layout>
                
                <!-- by teams -->
                <v-layout v-if="recipientType==2" row wrap>
                    <v-flex xs12 sm12 class="form-content">
                        <v-autocomplete
                            v-model="teamIds"
                            :disabled="disabled"
                            :items="teams"
                            :loading="loading_teams"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            label="Selecte Teams"
                            item-text="name"
                            item-value="teamId"
                            multiple
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                <v-list-tile-content>
                                    <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                    <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>

                <!-- by employees -->
                <v-layout v-if="recipientType==3" row wrap>
                    <v-flex xs12 sm12 class="form-content">
                        <v-autocomplete
                            v-model="userIds"
                            :disabled="disabled"
                            :items="employees"
                            :loading="loading_employees"
                            :rules="[rules.required]"
                            chips
                            color="primary lighten-2"
                            label="Select Employees"
                            item-text="name"
                            item-value="userId"
                            multiple
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi ml-0">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="data.item.avatar"></v-img>
                                    </v-avatar>
                                    {{ data.item.name }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                <v-list-tile-avatar>
                                    <img :src="data.item.avatar">
                                </v-list-tile-avatar>
                                <v-list-tile-content>
                                    <v-list-tile-title v-html="data.item.name"></v-list-tile-title>
                                    <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>

                <!-- upload section -->
                <v-layout row wrap>
                    <v-flex v-if="!uploadFileName" xs12 sm12 class="form-content">
                        <div class="text-xs-center">
                            <vue-dropzone ref="myVueDropzone" 
                                class="form-upload"
                                id="dropzone" 
                                :options="dropzoneOptions"
                                v-on:vdropzone-success="uploadSuccess"
                                v-on:vdropzone-file-added="fileAdded"
                                ></vue-dropzone>
                        </div>
                    </v-flex> 
                    <v-flex v-else xs12 sm12 class="form-content uploaded text-xs-left">
                        <span class="grey--text">Uploaded file: </span>
                        <span class="wrapped-filename">
                            {{ uploadFileName }}
                        </span>
                        <div v-if="hasDeleteRights" class="delete-container">
                            <v-btn icon small @click="showDeleteFile" :disabled="disabled" title="Delete uploaded file">
                                <v-icon small>delete</v-icon>
                            </v-btn>
                        </div>
                    </v-flex>
                </v-layout>

                <!-- description (optional)  -->
                <v-layout row wrap v-if="showNotes"> 
                    <v-flex xs12 sm12 class="form-content"> 
                        <v-textarea v-model="versionNotes" label="Notes" rows="1" color="primary"></v-textarea>
                    </v-flex>
                </v-layout>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="mr-25">
                <v-spacer></v-spacer>
                <v-btn icon @click="showNotes=!showNotes" title="Add Notes">
                    <v-icon>comment</v-icon>
                </v-btn>
                <v-btn type="submit" class="primary" :disabled="disabled||loading||!completeFormData" :loading="loading">Save</v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="dialogOkCancel" persistent width="600">
            <ok-cancel :msg="dialogOkCancelMsg" :dialogOkCancel.sync="dialogOkCancel" @ok="onOkCancel"></ok-cancel>
        </v-dialog>
    </v-form>
</template>
<script>
    
import edocumentsApi from '@/api/edocuments'
import teamsApi from '@/api/team'
import employeeApi from '@/api/employees'
import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import pageKeys from '@/common/pagekeys'
import awsS3Helper from '@/helpers/aws-s3'
import { hasDeleteRights } from '@/helpers/access-permissions'

export default {
    name: 'AddEditDocumentVersion',
    components: {
        vueDropzone: vue2Dropzone,
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
    },
    data () {
        return {
            error: false,
            errorMessage: '',
            disabled: false,
            loading: false,
            loading_uploading: false,
            loading_categories: false,
            loading_employees: false,
            loading_teams: false,

            //put this in rules-helper.js
            rules: {
                required: value => !!value || 'Required.',
                required_list: value => !!value.length || 'Required.',
                required_multiple: value => value.length>0 || 'Must select at least one month.',
            },

            recipient_types: [
                { recipientType: 1, description: 'All Employees' },
                { recipientType: 2, description: 'Selected Teams' },
                { recipientType: 3, description: 'Selected Employees' }
            ],
            showNotes: false,
            mode: '',
            categories: [],
            types: [],
            employees: [],
            existing_users: [],

            documentVersionId: null,
            documentId: null,
            versionLabel: null,
            versionNotes: null,
            recipientType: null,
            currentVersionNumber: null,
            teams: [],
            teamIds: [],
            userIds: [],
            uploadFileName: null,
            uploadFileType: null,

            //okcancel
            dialogOkCancel: false, 
            dialogOkCancelMsg: '',
            okCancelFunction: null,
        }
    },
    methods: {
        async saveDocumentVersion() {
            try {
                this.loading = true
                this.disabled = true

                if (this.$refs.form.validate() && this.completeFormData) {
                    const data = {
                        documentVersionId: this.documentVersionId,
                        documentId: this.documentId,
                        versionLabel: this.versionLabel,
                        versionNotes: this.versionNotes,
                        recipientType: this.recipientType,
                        versionNumber: this.currentVersionNumber + 1,
                        fileName: this.uploadFileName,
                        fileType: this.uploadFileType,
                        teamIds: this.teamIds,
                        userIds: this.userIds
                    }

                    let response 
                    if (this.isAddNewMode)
                        response = await edocumentsApi.addVersion(data)
                    else
                        response = await edocumentsApi.updateVersion(data)

                    if (response && response.status == 200) {
                        this.$emit('send', response.data)
                        this.$emit('update:dialogAddEditVersion', false)
                    }
                }
                this.loading = false
                this.disabled = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        fileAdded() {
            this.loading_uploading = true
        },
        uploadSuccess(file, response) {
            this.$refs.myVueDropzone.removeAllFiles(true)
            this.uploadFileName = response.fileName
            this.uploadFileType = response.fileType
            this.loading_uploading = false
        },
        async getTeams() {
            try {
                this.loading_teams = true
                const response = await teamsApi.getTeamsByCompany(false)
                if (response && response.status == 200) {
                    this.teams = response.data.items
                }
                this.loading_teams = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getEmployees() {
            try {
                this.loading_employees = true
                this.employees = []
                const response = await employeeApi.getAllByPageKey(pageKeys.edocuments)
                if (response && response.status == 200) {
                    //exclude those already exist
                    const filtered = response.data.employees.filter(user => {
                        return !this.existing_users.some(x => x.parentUserId == user.userId) 
                    });
                    filtered.forEach(item => {
                        this.employees.push({
                            userId: item.userId,
                            name: item.firstName + " " + item.lastName,
                            avatar: awsS3Helper.getAvatarUrl(item.userId),
                            firstName: item.firstName,
                            lastName: item.lastName,
                            email: item.email,
                            group: item.team
                        })
                    })
                }
                this.loading_employees = false
            }catch(error) {
                this.errorMessage = error
                this.error = true
            }
        },
        async deleteFile() {
            try {
                //this deletes only the uploaded file
                const data = {
                    fileName: this.uploadFileName
                }
                const response = await edocumentsApi.deleteFile(data)
                if (response && response.status == 200) {
                    this.uploadFileName = null
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        setAdd(data) {
            this.clearForm()
            this.mode = 'Add'
            this.documentId = data.documentId
            this.currentVersionNumber = data.currentVersionNumber
            this.initializeData()
        },
        async setEdit(data) {
            this.mode = 'Update'
            await this.initializeData()

            //set existing data
            this.teamIds = data.teamIds,
            this.userIds = data.userIds,
            this.documentVersionId = data.documentVersionId,
            this.documentId = data.documentId,
            this.versionLabel = data.versionLabel,
            this.versionNotes = data.versionNotes,
            this.recipientType = data.recipientType,
            this.uploadFileName = data.fileName,
            this.uploadFileType = data.fileType,
            this.versionNumber = data.versionNumber
        },
        async initializeData() {
            if (!this.teams.length) {
                await this.getTeams()
            }
            if (!this.employees.length) {
                await this.getEmployees()
            }
        },
        onOkCancel() {
            if (typeof this.okCancelFunction === 'function') {
                this.okCancelFunction()
            }
            this.okCancelFunction = null
            this.dialogOkCancel = false
        },
        async closeDialog() {
            if (this.uploadFileName && this.isAddNewMode) {
                this.dialogOkCancelMsg = 'You have uploaded a file, but have not saved its details.<br>Closing this window will delete the uploaded file.<br>Do you want to proceed?'
                this.dialogOkCancel = true
                this.okCancelFunction = () => this.deleteUploadAndClosePopup() //no need to check hasDeleteRights here since no record has been saved yet
            }else{
                this.$emit('update:dialogAddEditVersion', false)
            }
        },
        clearForm() {
            this.$refs.form.reset()
            this.fileUploadId = null
            this.uploadFileName = null
            this.uploadFileType = null
            this.error = false
            this.errorMessage = ''
            this.loading = false
        },
        async deleteUploadAndClosePopup() {
            await this.deleteFile()
            this.$emit('update:dialogAddEditVersion', false)
        },
        showDeleteFile() {
            this.dialogOkCancelMsg = 'This action will delete the uploaded file.<br> Do you want to proceed?'
            this.dialogOkCancel = true
            this.okCancelFunction = () => {
                if (this.mode == 'Add') {
                    this.deleteFile()
                }else{
                    //do not delete the file in AWS directly so that when user cancels edit
                    //the file still exist
                    this.uploadFileName = null
                }
            }
        },
    },
    computed: {
        hasDeleteRights() {
            return hasDeleteRights(this.$router.currentRoute.path)
        },
        dropzoneOptions() {
            return {                    
                url: `${axios.defaults.baseURL}EDocument/upload`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.edocuments,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                addRemoveLinks: false,
                dictDefaultMessage: `<u>
                                        ${this.isMobile ? 'Tap to browse' : 'Drag and drop your file or click to browse'}
                                    </u>
                                    <p class='caption mt-5'>
                                        Maximum 10MB per file.<br>
                                        Accepted file types: Images (.jpg, .jpeg, .png) and PDFs (.pdf)
                                    </p>`,
                maxFiles: 1,
                acceptedFiles: [
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'application/pdf'
                    ].join(','),
                maxFilesize: 10,   // 10MB
            }
        },
        isAddNewMode() {
            return this.mode == 'Add'
        },
        completeFormData() {
            return this.versionLabel &&
                this.uploadFileName &&
                (
                    this.recipientType == 1 ||
                    (this.recipientType == 2 && this.teamIds.length) ||
                    (this.recipientType == 3 && this.userIds.length)
                ) 
        },
    }
}
</script>
<style scoped>
.form-content {
    padding:0px 20px 0px 20px;
    margin-top:-5x;
}
.form-upload {
    height:150px;
    margin:15px 0px 10px 0px;
    padding:0px 50px 0px 50px;
    border: 1px dashed darkgray
}
.delete-container {
    float:right;
    margin-top:-10px;
}
.uploaded {
    margin-top: 15px;
}
</style>